/* eslint-disable no-comments/disallowComments */
import React from 'react';
import { styled } from '@mui/material';

type RStackGap = 'none' | 'sm' | 'md' | 'lg';
type RStackMaxWidth = 'sm' | 'md' | 'lg' | 'xl';
type RStackAlign = 'start' | 'center' | 'end' | 'stretch';
type RStackJustify = 'start' | 'center' | 'end' | 'between';
type RStackComponent = 'div' | 'section' | 'article' | 'aside' | 'main' | 'header' | 'footer';

interface RStackProps {
    children: React.ReactNode;
    /** Gap between children: `none` (0), `sm` (8px), `md` (16px, default), or `lg` (24px). */
    gap?: RStackGap;
    /** Cap on stack width: `sm` (480px), `md` (800px), `lg` (1200px), or `xl` (1400px). Omit for no cap. */
    maxWidth?: RStackMaxWidth;
    /** Cross-axis alignment (horizontal). Defaults to `stretch`. */
    align?: RStackAlign;
    /** Main-axis alignment (vertical). Defaults to `start`. */
    justify?: RStackJustify;
    /** If true, stretches to 100% of parent height. Useful inside flex parents. */
    fullHeight?: boolean;
    /** If true, applies `flex: 1` + `minWidth: 0` so the stack grows to fill a flex-row parent. */
    grow?: boolean;
    /** HTML element to render. Defaults to `div`. */
    component?: RStackComponent;
    className?: string;
}

const GAP_PX: Record<RStackGap, number> = { none: 0, sm: 8, md: 16, lg: 24 };
const MAX_WIDTH_PX: Record<RStackMaxWidth, number> = { sm: 480, md: 800, lg: 1200, xl: 1400 };
const ALIGN_VALUE: Record<RStackAlign, string> = {
    start: 'flex-start',
    center: 'center',
    end: 'flex-end',
    stretch: 'stretch',
};
const JUSTIFY_VALUE: Record<RStackJustify, string> = {
    start: 'flex-start',
    center: 'center',
    end: 'flex-end',
    between: 'space-between',
};

type StackStyleProps = {
    $gap: RStackGap;
    $maxWidth?: RStackMaxWidth;
    $align: RStackAlign;
    $justify: RStackJustify;
    $fullHeight: boolean;
    $grow: boolean;
};

const stackStyles = ({ $gap, $maxWidth, $align, $justify, $fullHeight, $grow }: StackStyleProps) => ({
    display: 'flex',
    flexDirection: 'column' as const,
    gap: GAP_PX[$gap],
    alignItems: ALIGN_VALUE[$align],
    justifyContent: JUSTIFY_VALUE[$justify],
    maxWidth: $maxWidth ? MAX_WIDTH_PX[$maxWidth] : undefined,
    height: $fullHeight ? '100%' : undefined,
    flex: $grow ? 1 : undefined,
    minWidth: $grow ? 0 : undefined,
});

const shouldForwardStackProp = (prop: string) =>
    !['$gap', '$maxWidth', '$align', '$justify', '$fullHeight', '$grow'].includes(prop);

const DivStack = styled('div', { shouldForwardProp: (p) => shouldForwardStackProp(p as string) })<StackStyleProps>(
    stackStyles,
);
const SectionStack = styled('section', {
    shouldForwardProp: (p) => shouldForwardStackProp(p as string),
})<StackStyleProps>(stackStyles);
const ArticleStack = styled('article', {
    shouldForwardProp: (p) => shouldForwardStackProp(p as string),
})<StackStyleProps>(stackStyles);
const AsideStack = styled('aside', { shouldForwardProp: (p) => shouldForwardStackProp(p as string) })<StackStyleProps>(
    stackStyles,
);
const MainStack = styled('main', { shouldForwardProp: (p) => shouldForwardStackProp(p as string) })<StackStyleProps>(
    stackStyles,
);
const HeaderStack = styled('header', {
    shouldForwardProp: (p) => shouldForwardStackProp(p as string),
})<StackStyleProps>(stackStyles);
const FooterStack = styled('footer', {
    shouldForwardProp: (p) => shouldForwardStackProp(p as string),
})<StackStyleProps>(stackStyles);

const COMPONENTS = {
    div: DivStack,
    section: SectionStack,
    article: ArticleStack,
    aside: AsideStack,
    main: MainStack,
    header: HeaderStack,
    footer: FooterStack,
};

export function RStack({
    children,
    gap = 'md',
    maxWidth,
    align = 'stretch',
    justify = 'start',
    fullHeight = false,
    grow = false,
    component = 'div',
    className,
}: RStackProps) {
    const Component = COMPONENTS[component];
    return (
        <Component
            $gap={gap}
            $maxWidth={maxWidth}
            $align={align}
            $justify={justify}
            $fullHeight={fullHeight}
            $grow={grow}
            className={className}
        >
            {children}
        </Component>
    );
}
/* eslint-enable no-comments/disallowComments */
